import { useMemo } from 'react';
// utils
import { poster, endpoints, fetcher } from '../utils/axios';

/**
 * Authenticates a user using the provided payload.
 * @param {Object} payload - The payload containing user login credentials.
 * @returns {Promise} - A promise that resolves with the authentication response.
 */

export async function userLogin(payload) {
  const URL = endpoints.auth.login;
  return await poster([URL, payload])
}
/**
 * Logs out the user from the application.
 * @param {Object} payload - The payload containing the user's refresh token.
 * @returns {Promise} - A promise that resolves with the logout response.
 */
export async function userLogout(payload) {
  const URL = endpoints.auth.logout;
  return await poster([URL, payload])
}
/**
 * Resets the password for a user using the provided payload.
 * @param {Object} payload - The payload containing the user's email address and new password.
 * @returns {Promise} - A promise that resolves with the password reset response.
 */
export async function passwordReset(payload) {
  const URL = endpoints.auth.passwordReset;
  return await poster([URL, payload])
};
/**
 * Sends an email to the user with a link to reset the password.
 * @param {Object} payload - The payload containing the user's email address.
 * @returns {Promise} - A promise that resolves with the password reset response.
 */
export async function forgotPassword(payload) {
  const URL = endpoints.auth.forgotPassword;
  return await poster([URL, payload])
}
/**
 * Sends an email to the user with a link to reset the password using the provided payload.
 * @param {Object} payload - The payload containing the user's email address.
 * @returns {Promise} - A promise that resolves with the password reset response.
 */
export async function forgotPasswordWithLink(payload) {
  const URL = endpoints.auth.forgotPasswordWithLink;
  return await poster([URL, payload])
}

/**
 * Verifies the One-Time Password (OTP) for a user.
 * @param {Object} payload - The payload containing the OTP and related user information.
 * @returns {Promise} - A promise that resolves with the OTP verification response.
 */

export async function verifyOTP(payload) {
  const URL = endpoints.auth.verifyOTP;
  return await poster([URL, payload])
}

/**
 * Generates a login link for a user using the provided payload.
 * @param {Object} payload - The payload containing the user's email address and other relevant information.
 * @returns {Promise} - A promise that resolves with the login link response.
 */
export async function loginlink(payload) {
  const URL = endpoints.auth.loginlink;
  return await fetcher([URL, { params: payload }])
}


/**
 * Changes the user's password after a successful OTP verification.
 * @param {Object} payload - The payload containing the user's new password and other relevant information.
 * @returns {Promise} - A promise that resolves with the password change response.
 */
export async function changeNewPassword(payload) {
  const URL = endpoints.auth.changeNewPassword;
  return await poster([URL, payload])
}

/**
 * Verifies user credentials using the provided payload.
 * @param {Object} payload - The payload containing user credentials for verification.
 * @returns {Promise} - A promise that resolves with the verification response.
 */

export async function userVerify(payload) {
  const URL = endpoints.auth.verifyOTP;
  return await poster([URL, payload])
}

